import { useEffect, useState } from "react";
import {
  Heart,
  Footprints,
  ScrollText,
  Flame,
  Sparkles,
} from "lucide-react";

// Server regen rates (mirror `apply_regen` in server.py).
const EP_INTERVAL_S = 60;
const QP_INTERVAL_S = 300;
const POOL_INTERVAL_S = 45;

function formatCountdown(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) {
    return "";
  }

  const s = Math.max(0, Math.ceil(seconds));

  if (s < 60) {
    return `${s}s`;
  }

  const m = Math.floor(s / 60);
  const rs = s % 60;

  return rs === 0 ? `${m}m` : `${m}m ${rs}s`;
}

function useNextTick(lastRegenIso, interval) {
  const calculateRemaining = () => {
    if (!lastRegenIso) {
      return null;
    }

    const last = new Date(lastRegenIso).getTime();

    if (Number.isNaN(last)) {
      return null;
    }

    const now = Date.now();
    const elapsed = Math.max(0, (now - last) / 1000);

    const remainder = elapsed % interval;
    const remaining = interval - remainder;

    return Math.ceil(remaining);
  };

  const [remaining, setRemaining] = useState(calculateRemaining);

  useEffect(() => {
    const update = () => {
      setRemaining(calculateRemaining());
    };

    update();

    const timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [lastRegenIso, interval]);

  return remaining;
}

function Bar({
  value,
  max,
  color,
  testid,
  Icon,
  label,
  timer,
}) {
  const safeValue = value ?? 0;
  const safeMax = max ?? 0;

  const pct =
    safeMax > 0
      ? Math.max(
          0,
          Math.min(100, (safeValue / safeMax) * 100)
        )
      : 0;

  const full = safeMax > 0 && safeValue >= safeMax;

  return (
    <div
      className="flex items-center gap-2 flex-1"
      data-testid={testid}
      title={label}
    >
      <Icon
        className="w-3 h-3"
        style={{ color }}
        strokeWidth={1.5}
      />

      <div className="flex-1 vbar">
        <span
          style={{
            width: `${pct}%`,
            background: color,
          }}
        />
      </div>

      <span className="font-mono text-[10px] text-[#8c8c8c] w-16 text-right">
        {safeValue}/{safeMax}
      </span>

      {timer !== undefined && timer !== null && (
        <span
          className="font-mono text-[9px] w-16 flex items-center justify-end gap-1"
          style={{
            color: full ? "#4A5D23" : color,
            opacity: full ? 0.7 : 1,
          }}
          data-testid={`${testid}-timer`}
        >
          {full ? (
            "full"
          ) : (
            <>
              <button
                type="button"
                className="
                  px-1.5
                  h-4
                  rounded-sm
                  border
                  font-bold
                  text-[8px]
                  leading-none
                  transition-all
                  duration-100
                  hover:brightness-125
                  hover:shadow-[0_0_6px_currentColor]
                  hover:-translate-y-px
                  active:translate-y-0
                  active:scale-95
                  select-none
                "
                style={{
                  borderColor: color,
                  color: color,
                  background: "rgba(255,255,255,0.04)",
                  boxShadow: `0 0 4px ${color}55`,
                }}
                onClick={() => {
                  // TODO: collegare qui l'azione del +1
                }}
              >
                +1
              </button>

              <span>{formatCountdown(timer)}</span>
            </>
          )}
        </span>
      )}
    </div>
  );
}

export default function VitalsHeader({ ch }) {
  const xp = ch?.xp ?? 0;
  const xpNext = ch?.xp_next ?? 0;

  const xpPct =
    xpNext > 0
      ? Math.min(100, (xp / xpNext) * 100)
      : 0;

  const hasMana = (ch?.max_mana ?? 0) > 0;
  const hasVigor = (ch?.max_vigor ?? 0) > 0;

  const epNext = useNextTick(
    ch?.last_ep_regen_at || ch?.last_regen_at,
    EP_INTERVAL_S
  );

  const qpNext = useNextTick(
    ch?.last_regen_at,
    QP_INTERVAL_S
  );

  const poolNext = useNextTick(
    ch?.last_regen_at,
    POOL_INTERVAL_S
  );

  return (
    <div className="px-3 pb-2 space-y-1">
      <Bar
        label="HP"
        value={ch?.hp ?? 0}
        max={ch?.max_hp ?? 0}
        color="#8A0303"
        testid="vitals-hp"
        Icon={Heart}
      />

      <Bar
        label="Explore Points"
        value={ch?.explore_points ?? 0}
        max={ch?.max_explore_points ?? 0}
        color="#E55A00"
        testid="vitals-explore"
        Icon={Footprints}
        timer={epNext}
      />

      {hasVigor && (
        <Bar
          label="Vigor"
          value={ch?.vigor ?? 0}
          max={ch?.max_vigor ?? 0}
          color="#B89C3A"
          testid="vitals-vigor"
          Icon={Flame}
          timer={poolNext}
        />
      )}

      {hasMana && (
        <Bar
          label="Mana"
          value={ch?.mana ?? 0}
          max={ch?.max_mana ?? 0}
          color="#4A7BC7"
          testid="vitals-mana"
          Icon={Sparkles}
          timer={poolNext}
        />
      )}

      <Bar
        label="Quest Points"
        value={ch?.quest_points ?? 0}
        max={ch?.max_quest_points ?? 0}
        color="#2A4B7C"
        testid="vitals-qp"
        Icon={ScrollText}
        timer={qpNext}
      />

      <div className="flex items-center justify-between pt-1">
        <div
          className="flex-1 vbar"
          data-testid="vitals-xp"
        >
          <span
            style={{
              width: `${xpPct}%`,
              background: "#B89C3A",
            }}
          />
        </div>

        <span className="font-mono text-[10px] text-[#8c8c8c] ml-2">
          XP {xp}/{xpNext}
        </span>

        <span
          className="font-mono text-[10px] text-[#E5C34A] ml-3"
          data-testid="hero-gold"
        >
          {ch?.gold ?? 0}g
        </span>
      </div>
    </div>
  );
}